import { isTauri } from "./desktopBridge.js";

/**
 * Tracks whether the app window is currently **focused / in active use**, so
 * the chat layer can choose between an unobtrusive in-app cue (focused) and a
 * real OS desktop notification (not focused — minimised, hidden in the tray,
 * in the background, or on another virtual desktop / Space).
 *
 * Two sources, by runtime:
 *
 * - **Web:** derived synchronously from the document (visibility + focus). It
 *   is always current, so no listener wiring is required — `isAppFocused()`
 *   just reads the document each call.
 * - **Desktop (Tauri):** the NATIVE window-focus signal is authoritative. The
 *   embedded webview's `document.hasFocus()` can report stale / incorrect
 *   focus (WKWebView keeps internal focus even when the OS window is in the
 *   background), so once {@link initAppFocusTracking} has seeded the native
 *   state we trust Tauri's `onFocusChanged` over the document.
 */
let nativeFocused: boolean | null = null;

/** The web (document-derived) focus signal: the tab is visible AND has focus. */
function webFocused(): boolean {
  if (typeof document === "undefined") return false;
  const visible = document.visibilityState !== "hidden";
  const hasFocus = typeof document.hasFocus === "function" ? document.hasFocus() : true;
  return visible && hasFocus;
}

/**
 * Whether the app is focused / in active use right now. Synchronous so the
 * realtime chat handler can branch on it the instant a message arrives.
 *
 * In the desktop app this returns the native window-focus state once
 * {@link initAppFocusTracking} has run; before that (and on the web) it falls
 * back to the live document state.
 */
export function isAppFocused(): boolean {
  if (nativeFocused !== null) return nativeFocused;
  return webFocused();
}

/**
 * Wire native focus tracking for the desktop shell. On the web there is
 * nothing to wire (`isAppFocused()` reads the document directly), so this is a
 * no-op there. Call once on app mount; returns a cleanup that detaches the
 * listener and clears the cached state.
 */
export async function initAppFocusTracking(): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  try {
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    const w = getCurrentWindow();
    try {
      nativeFocused = await w.isFocused();
    } catch {
      /* older shell without isFocused() — leave null, fall back to the document */
    }
    const unlisten = await w.onFocusChanged(({ payload }) => {
      nativeFocused = payload;
    });
    return () => {
      unlisten();
      nativeFocused = null;
    };
  } catch (e) {
    console.warn("[desktop] focus tracking unavailable:", e);
    return () => undefined;
  }
}
